import React, { useState } from 'react';
import { ChevronLeft, Check, Building2, FileText, Users, Link2, Calculator, Edit2, Home } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';

const Section = ({ title, icon: Icon, stepId, children, onGoToStep }) => (
  <div className="bg-slate-50 rounded-xl p-5">
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-semibold text-slate-900 flex items-center gap-2">
        <Icon className="w-5 h-5 text-primary" />
        {title}
      </h3>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onGoToStep(stepId)}
        className="text-primary hover:text-primary"
      >
        <Edit2 className="w-4 h-4 mr-1" /> Editar
      </Button>
    </div>
    <div className="space-y-2 text-sm">
      {children}
    </div>
  </div>
);

const DataRow = ({ label, value, highlight }) => (
  <div className="flex justify-between py-1 border-b border-slate-200 last:border-0">
    <span className="text-slate-600">{label}</span>
    <span className={`font-medium ${highlight ? 'text-primary' : 'text-slate-900'}`}>
      {value || <span className="text-slate-400">No especificado</span>}
    </span>
  </div>
);

export default function ReviewStep({ data, onNext, onBack, onGoToStep, saving, accountingMode }) {
  const [confirmed, setConfirmed] = useState(false);

  const fiscal = data.fiscal_data || {};
  const billing = data.billing_data || {};
  const contacts = data.contacts_data || {};
  const accounting = data.accounting_data || {};
  const holded = data.holded_data || {};

  const mode = accountingMode || accounting.accounting_mode;
  const isNativeMode = mode === 'native';

  const handleSubmit = () => {
    if (confirmed) {
      onNext(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-1">Revision Final</h2>
        <p className="text-slate-600">Revisa todos los datos antes de completar la incorporacion</p>
      </div>

      {/* Progress summary */}
      <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-xl">
        <div className="w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center">
          <Check className="w-5 h-5" />
        </div>
        <div>
          <div className="font-semibold text-green-900">Casi listo!</div>
          <p className="text-sm text-green-700">Has completado {data.progress_percentage}% de la incorporacion</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Datos Fiscales */}
        <Section title="Datos Fiscales" icon={Building2} stepId={2} onGoToStep={onGoToStep}>
          <DataRow label="Razon Social" value={fiscal.razon_social} highlight />
          <DataRow label="NIF/CIF" value={fiscal.nif_cif} />
          <DataRow label="Pais" value={fiscal.pais_fiscal} />
          <DataRow label="Ciudad" value={`${fiscal.codigo_postal} ${fiscal.ciudad}`} />
          <DataRow label="Regimen IVA" value={fiscal.regimen_iva?.replace('_', ' ')} />
          {fiscal.aplica_retencion && (
            <DataRow label="Retencion IRPF" value={`${fiscal.porcentaje_retencion}%`} />
          )}
          {fiscal.intracomunitario && (
            <DataRow label="NIF-VAT" value={fiscal.nif_vat_intra} />
          )}
        </Section>

        {/* Facturacion */}
        <Section title="Facturacion" icon={FileText} stepId={3} onGoToStep={onGoToStep}>
          <DataRow label="Serie" value={billing.serie_factura} highlight />
          <DataRow label="Numero Inicial" value={billing.numero_inicial} />
          <DataRow label="Email Facturacion" value={billing.email_factura} />
          <DataRow label="Metodo de Cobro" value={billing.metodo_cobro_defecto?.replace('_', ' ')} />
          {billing.iban_mostrar && (
            <DataRow label="IBAN" value={`...${billing.iban_mostrar.slice(-4)}`} />
          )}
          <DataRow label="Dias Vencimiento" value={`${billing.dias_vencimiento_defecto} dias`} />
          {billing.logo_empresa && (
            <div className="flex items-center gap-2 pt-2">
              <Badge variant="secondary" className="text-xs">Logo subido</Badge>
            </div>
          )}
        </Section>

        {/* Contactos */}
        <Section title="Contactos" icon={Users} stepId={4} onGoToStep={onGoToStep}>
          <DataRow label="Administrador" value={contacts.admin_nombre} highlight />
          <DataRow label="Email Admin" value={contacts.admin_email} />
          {contacts.admin_telefono && (
            <DataRow label="Telefono Admin" value={contacts.admin_telefono} />
          )}
          {contacts.contacto_secundario_nombre && (
            <DataRow label="Contacto Secundario" value={contacts.contacto_secundario_nombre} />
          )}
          {contacts.gestoria_nombre && (
            <>
              <div className="border-t border-slate-300 my-2" />
              <DataRow label="Gestoria" value={contacts.gestoria_nombre} />
              <DataRow label="Contacto Gestoria" value={contacts.gestoria_contacto} />
              <DataRow label="Email Gestoria" value={contacts.gestoria_email} />
            </>
          )}
        </Section>

        {/* Modo de Contabilidad */}
        <Section title="Modo de Contabilidad" icon={Calculator} stepId={5} onGoToStep={onGoToStep}>
          <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
            {isNativeMode ? (
              <>
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Home className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">Contabilidad Nativa Zentro</div>
                  <p className="text-xs text-slate-500">Modulos integrados de facturacion y gastos</p>
                </div>
              </>
            ) : (
              <>
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Link2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">Integracion con Holded</div>
                  <p className="text-xs text-slate-500">Sincronizacion con cuenta de Holded</p>
                </div>
              </>
            )}
          </div>
        </Section>

        {/* Holded - Only show if holded mode */}
        {!isNativeMode && (
          <Section title="Integracion Holded" icon={Link2} stepId={6} onGoToStep={onGoToStep}>
            <DataRow
              label="Metodo de Acceso"
              value={holded.holded_access_method === 'invite' ? 'Invitacion' : 'Clave API'}
              highlight
            />
            {holded.holded_access_method === 'invite' && (
              <DataRow label="Email Invitacion" value={holded.holded_user_email} />
            )}
            {holded.holded_access_method === 'api_key' && (
              <DataRow label="Clave API" value="********************" />
            )}
            <DataRow
              label="Permisos"
              value={holded.holded_permissions_granted?.join(', ') || 'Ninguno'}
            />
            <DataRow
              label="Sincronizacion"
              value={holded.holded_sync_frequency === 'realtime' ? 'Tiempo real' :
                     holded.holded_sync_frequency === 'daily' ? 'Diaria' : 'Semanal'}
            />
          </Section>
        )}

        {/* Native Mode Features - Only show if native mode */}
        {isNativeMode && (
          <Section title="Funcionalidades Nativas" icon={Home} stepId={5} onGoToStep={onGoToStep}>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                <span>Modulo de facturacion integrado</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                <span>Generacion de PDFs automaticos</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                <span>Registro de gastos</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                <span>Exportacion a Excel/CSV</span>
              </div>
            </div>
          </Section>
        )}
      </div>

      {/* Confirmacion */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 space-y-4">
        <div className="flex items-start space-x-3">
          <Checkbox
            id="confirm"
            checked={confirmed}
            onCheckedChange={setConfirmed}
            className="mt-0.5"
            data-testid="checkbox-confirm"
          />
          <Label htmlFor="confirm" className="cursor-pointer text-amber-900">
            <span className="font-semibold">Confirmo que todos los datos son correctos</span>
            <p className="text-sm text-amber-700 mt-1">
              Al confirmar, aceptas que la informacion proporcionada es veraz y autorizas a Zentro
              a gestionar tus servicios administrativos en base a estos datos.
              {isNativeMode && (
                <span className="block mt-1">
                  Utilizaras los modulos nativos de Zentro para facturacion y gastos.
                </span>
              )}
            </p>
          </Label>
        </div>
      </div>

      <div className="flex justify-between pt-6 border-t">
        <Button variant="outline" onClick={onBack} data-testid="btn-back">
          <ChevronLeft className="w-4 h-4 mr-2" /> Atras
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!confirmed || saving}
          className="bg-green-600 hover:bg-green-700"
          data-testid="btn-complete"
        >
          {saving ? 'Completando...' : 'Completar incorporacion'} <Check className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}

